import React, { useEffect, useRef, type JSXElementConstructor } from 'react';
import { PreviewerItem } from './previewerItem';
import { Hooks } from '#packages/apilib';
import type { DisplayConfig, PreviewerEffectsMapping } from '../types';
import styles from './preview.scss';

interface PreviewerItemData {
  title: string;
  ThumbnailComponent: JSXElementConstructor<{ onReady: Function }>;
  effectsMapping: PreviewerEffectsMapping;
  previewIndicator?: React.ReactElement;
}

interface PreviewProps {
  previewItems: PreviewerItemData[];
  displayConfig: DisplayConfig;
  onReady?: () => void;
}

export const Preview = ({
  previewItems,
  displayConfig,
  onReady,
}: PreviewProps) => {
  const {
    displayWidth,
    baseWidth,
    displayMaxHeight,
    shouldOverlay,
    isInteractive,
    isScrollable,
    isClickable,
  } = displayConfig;
  const previewRef = useRef<HTMLDivElement>(null);
  const enablePreviewAnimationsPromiseRef = useRef<Hooks.PromiseHook>(null);
  const awaitingReady = useRef<number>(previewItems?.length);

  useEffect(() => {
    enablePreviewAnimationsPromiseRef.current = Hooks.createPromiseHook();
    awaitingReady.current = previewItems?.length;
  }, [previewItems, displayConfig]);

  function onPreviewItemReady(): () => Promise<void> {
    return async () => {
      awaitingReady.current--;
      console.log(`${awaitingReady.current} previews left to be ready`);
      if (awaitingReady.current === 0) {
        enablePreviewAnimationsPromiseRef.current.resolve();
        onReady?.();
      }
    };
  }

  return (
    <div className={styles.preview} ref={previewRef}>
      {previewItems.map(
        ({ ThumbnailComponent, effectsMapping, previewIndicator }, index) => (
          <div key={index} className={styles.previewItemWrapper}>
            <PreviewerItem
              ThumbnailComponent={ThumbnailComponent}
              displayWidth={displayWidth}
              baseWidth={baseWidth}
              displayMaxHeight={displayMaxHeight}
              shouldOverlay={shouldOverlay}
              isInteractive={isInteractive}
              isScrollable={isScrollable}
              isClickable={isClickable}
              enablePreviewAnimationsPromise={
                enablePreviewAnimationsPromiseRef.current?.promise
              }
              effectsMapping={effectsMapping}
              onReady={onPreviewItemReady()}
            />
            {previewIndicator}
          </div>
        ),
      )}
    </div>
  );
};
